import { useState } from "react";
import { Link } from "react-router-dom";
import { Minus, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { api, imgUrl, money } from "../lib/api";
import { FadeUp } from "../components/Reveal";
import { useCart } from "../context/CartContext";

export default function Cart() {
  const { items, setQty, remove, clear, subtotal } = useCart();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const submitRequest = async (e) => {
    e.preventDefault();
    setSending(true);
    const lines = items.map((i) => `• ${i.title || "Untitled print"} × ${i.qty}${i.price != null ? ` (${money(i.price)} each)` : ""}`).join("\n");
    try {
      await api.post("/inquiries/contact", {
        name: form.name,
        email: form.email,
        inquiry_type: "PRINT QUESTION",
        message: `PURCHASE REQUEST\n${lines}\n\n${form.message}`,
      });
      toast.success("Purchase request sent — Kenny will confirm availability and details.");
      clear();
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="pt-32 sm:pt-28 px-5 sm:px-8 pb-20 max-w-3xl" data-testid="cart-page">
      <FadeUp>
        <h1 className="font-display text-6xl sm:text-7xl tracking-wide leading-none" data-testid="cart-title">CART</h1>
      </FadeUp>

      {items.length === 0 ? (
        <FadeUp delay={0.1} className="mt-10">
          <p className="text-smoke text-sm" data-testid="cart-empty">Your cart is empty.</p>
          <Link to="/prints" data-testid="cart-shop-link" className="mt-6 h-12 px-7 inline-flex items-center bg-bone text-ink text-sm font-extrabold tracking-[0.18em] hover:bg-pink hover:text-white transition-colors duration-200">
            SHOP PRINTS
          </Link>
        </FadeUp>
      ) : (
        <>
          <div className="mt-8 divide-y divide-white/10 border-y border-white/10">
            {items.map((i) => (
              <div key={i.print_id} className="py-4 flex gap-4 items-center" data-testid={`cart-item-${i.print_id}`}>
                {i.image && (
                  <img src={imgUrl(i.image)} alt={i.title || "Print"} className="w-16 h-16 object-cover border border-white/10 artwork-view" draggable={false} onContextMenu={(e) => e.preventDefault()} />
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-display text-lg tracking-wide leading-none truncate">{i.title || "UNTITLED"}</p>
                  <p className="text-xs text-smoke mt-1">{i.price != null ? money(i.price) : "PRICE TBA"}</p>
                </div>
                <div className="flex items-center border border-white/20">
                  <button onClick={() => setQty(i.print_id, i.qty - 1)} data-testid={`cart-dec-${i.print_id}`} aria-label="Decrease" className="w-9 h-9 flex items-center justify-center"><Minus size={14} /></button>
                  <span className="w-8 text-center text-sm font-bold">{i.qty}</span>
                  <button onClick={() => setQty(i.print_id, i.qty + 1)} data-testid={`cart-inc-${i.print_id}`} aria-label="Increase" className="w-9 h-9 flex items-center justify-center"><Plus size={14} /></button>
                </div>
                <button onClick={() => remove(i.print_id)} data-testid={`cart-remove-${i.print_id}`} aria-label="Remove" className="w-9 h-9 flex items-center justify-center text-smoke hover:text-pink transition-colors duration-200">
                  <X size={16} />
                </button>
              </div>
            ))}
          </div>

          <div className="mt-6 flex justify-between items-baseline">
            <span className="text-xs font-bold tracking-[0.25em] text-smoke">SUBTOTAL</span>
            <span className="font-display text-3xl" data-testid="cart-subtotal">{money(subtotal) || "—"}</span>
          </div>
          <p className="mt-3 text-xs text-smoke leading-relaxed" data-testid="cart-checkout-note">
            Online checkout is coming soon. Submit a purchase request and Kenny will confirm availability, edition numbering and shipping directly.
          </p>

          <form onSubmit={submitRequest} className="mt-8 space-y-4" data-testid="purchase-request-form">
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="NAME" data-testid="request-name"
              className="w-full h-12 bg-charcoal border border-white/15 px-4 text-sm outline-none focus:border-pink placeholder:text-smoke/60 placeholder:tracking-[0.15em]" />
            <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="EMAIL" data-testid="request-email"
              className="w-full h-12 bg-charcoal border border-white/15 px-4 text-sm outline-none focus:border-pink placeholder:text-smoke/60 placeholder:tracking-[0.15em]" />
            <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
              placeholder="MESSAGE (OPTIONAL)" rows={3} data-testid="request-message"
              className="w-full bg-charcoal border border-white/15 px-4 py-3 text-sm outline-none focus:border-pink placeholder:text-smoke/60 placeholder:tracking-[0.15em]" />
            <button type="submit" disabled={sending} data-testid="request-submit"
              className="w-full h-12 bg-bone text-ink text-sm font-extrabold tracking-[0.2em] hover:bg-pink hover:text-white transition-colors duration-200 disabled:opacity-50">
              {sending ? "SENDING…" : "REQUEST PURCHASE"}
            </button>
          </form>
        </>
      )}
    </div>
  );
}
